<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'

import {
  STATUS_LABELS,
  STATUS_TAG,
  cancelAsn,
  closeAsn,
  createAsn,
  deleteAsn,
  getAsn,
  listAsns,
  releaseAsn,
  updateAsn,
  type AsnInput,
  type AsnLineInput,
  type InboundStatus,
  type ListRow,
} from '../api/inbound'
import { listSkus, type Sku } from '../api/skus'
import { listSuppliers, type Supplier } from '../api/suppliers'
import { listWarehouses, type Warehouse } from '../api/warehouses'

const router = useRouter()

const rows = ref<ListRow[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
const notice = ref('')

const warehouses = ref<Warehouse[]>([])
const suppliers = ref<Supplier[]>([])
const skus = ref<Sku[]>([])
const refError = ref<unknown>(null)

// 参照下拉用 undefined 表示「未选」：0 会被 el-select 当成值显示成「0」，placeholder 就没了
const query = reactive({
  keyword: '',
  warehouse: undefined as number | undefined,
  supplier: undefined as number | undefined,
  status: '',
  page: 1,
  size: 20,
})

const formOpen = ref(false)
const editing = ref<ListRow | null>(null)
const saving = ref(false)
const formError = ref<unknown>(null)
const form = reactive({
  warehouse_id: undefined as number | undefined,
  supplier_id: undefined as number | undefined,
  expected_at: null as string | null,
  remark: '',
  lines: [] as AsnLineInput[],
})

/** 仓库/供应商下拉与表格展示共用：id → 「编码 名称」 */
function label(id: number, list: { id: number; code: string; name: string }[]): string {
  const hit = list.find((item) => item.id === id)
  return hit ? `${hit.code} ${hit.name}`.trim() : `#${id}`
}

const formErrorText = computed(() => {
  if (!form.warehouse_id) return '请选择仓库'
  if (!form.supplier_id) return '请选择供应商'
  if (!form.lines.length) return '请至少添加一行商品'
  for (const [i, line] of form.lines.entries()) {
    if (!line.sku_id) return `第 ${i + 1} 行请选择商品`
    if (!(line.qty > 0)) return `第 ${i + 1} 行数量必须大于 0`
  }
  return ''
})

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listAsns({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      warehouse: query.warehouse || undefined,
      supplier: query.supplier || undefined,
      status: query.status || undefined,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

async function loadRefs(): Promise<void> {
  refError.value = null
  try {
    warehouses.value = (await listWarehouses({ page: 1, size: 100 })).list
    suppliers.value = (await listSuppliers({ page: 1, size: 100 })).list
    skus.value = (await listSkus({ page: 1, size: 200 })).list
    if (!form.warehouse_id && warehouses.value.length) form.warehouse_id = warehouses.value[0].id
  } catch (e) {
    // 参照数据拿不到下拉会空，但必须把原因露出来（如未选货主时后端回 422），否则页面看着像没数据
    refError.value = e
  }
}

onMounted(() => {
  void load()
  void loadRefs()
})

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

function resetForm(): void {
  Object.assign(form, {
    warehouse_id: warehouses.value[0]?.id,
    supplier_id: suppliers.value[0]?.id,
    expected_at: null,
    remark: '',
    lines: [] as AsnLineInput[],
  })
}

function openCreate(): void {
  editing.value = null
  resetForm()
  formError.value = null
  formOpen.value = true
}

async function openEdit(row: ListRow): Promise<void> {
  editing.value = row
  resetForm()
  formError.value = null
  try {
    const d = await getAsn(row.id)
    form.warehouse_id = d.asn.warehouse_id
    form.supplier_id = d.asn.supplier_id
    form.expected_at = toLocal(d.asn.expected_at)
    form.remark = d.asn.remark
    form.lines = d.items.map((item) => ({ sku_id: item.sku_id, qty: item.expected_qty }))
  } catch (e) {
    formError.value = e
  }
  formOpen.value = true
}

/** 后端时间是 RFC3339（带时区偏移），取到秒并还原成本地墙钟时间给日期控件 */
function toLocal(iso: string | null): string | null {
  if (!iso) return null
  return `${iso.slice(0, 10)} ${iso.slice(11, 19)}`
}

function addLine(): void {
  form.lines.push({ sku_id: 0, qty: 1 })
}

function removeLine(index: number): void {
  form.lines.splice(index, 1)
}

async function save(): Promise<void> {
  if (formErrorText.value) return
  saving.value = true
  formError.value = null
  const input: AsnInput = {
    warehouse_id: form.warehouse_id ?? 0,
    supplier_id: form.supplier_id ?? 0,
    expected_at: form.expected_at,
    remark: form.remark.trim(),
    items: form.lines.map((line) => ({ sku_id: line.sku_id, qty: Number(line.qty) })),
  }
  try {
    if (editing.value) await updateAsn(editing.value.id, input)
    else await createAsn(input)
    formOpen.value = false
    await load(editing.value ? '入库单已更新' : '入库单已创建')
  } catch (e) {
    formError.value = e
  } finally {
    saving.value = false
  }
}

async function remove(row: ListRow): Promise<void> {
  notice.value = ''
  try {
    await deleteAsn(row.id)
    await load(`已删除入库单「${row.doc_no}」`)
  } catch (e) {
    error.value = e
  }
}

/** 状态流转统一走这里：成功刷新并提示，失败把错误挂到行下方的告警区 */
async function act(row: ListRow, action: 'release' | 'cancel' | 'close', text: string): Promise<void> {
  notice.value = ''
  error.value = null
  try {
    if (action === 'release') await releaseAsn(row.id)
    else if (action === 'cancel') await cancelAsn(row.id)
    else await closeAsn(row.id)
    await load(text)
  } catch (e) {
    error.value = e
  }
}

function open(row: ListRow): void {
  void router.push({ name: 'inbound-detail', params: { id: row.id } })
}

const canEdit = (status: InboundStatus) => status === 'draft'
const canCancel = (status: InboundStatus) => status === 'draft' || status === 'released'
const canClose = (status: InboundStatus) => status === 'receiving' || status === 'received'
</script>

<template>
  <div class="yc-inb">
    <header class="yc-inb__bar">
      <el-input
        v-model="query.keyword"
        class="yc-inb__keyword"
        placeholder="入库单号"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select v-model="query.warehouse" class="yc-inb__ref" placeholder="全部仓库" clearable @change="search">
        <el-option v-for="w in warehouses" :key="w.id" :label="label(w.id, warehouses)" :value="w.id" />
      </el-select>
      <el-select v-model="query.supplier" class="yc-inb__ref" placeholder="全部供应商" clearable @change="search">
        <el-option v-for="s in suppliers" :key="s.id" :label="label(s.id, suppliers)" :value="s.id" />
      </el-select>
      <el-select v-model="query.status" class="yc-inb__status" placeholder="全部状态" clearable @change="search">
        <el-option v-for="(text, value) in STATUS_LABELS" :key="value" :label="text" :value="value" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button v-perm="'wms:inbound:create'" type="primary" @click="openCreate">新建入库单</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-inb__notice">{{ notice }}</span>
    </header>

    <div v-if="error || refError" class="yc-inb__error">
      <TraceIdAlert :error="error ?? refError" />
    </div>

    <el-card v-loading="loading" class="yc-inb__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="doc_no" label="入库单号" width="170" />
        <el-table-column label="仓库" min-width="130">
          <template #default="{ row }">{{ row.warehouse_name || `#${row.warehouse_id}` }}</template>
        </el-table-column>
        <el-table-column label="供应商" min-width="150">
          <template #default="{ row }">{{ row.supplier_name || `#${row.supplier_id}` }}</template>
        </el-table-column>
        <el-table-column label="状态" width="96">
          <template #default="{ row }">
            <el-tag :type="STATUS_TAG[row.status as InboundStatus]" size="small">
              {{ STATUS_LABELS[row.status as InboundStatus] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="应收 / 已收" width="150">
          <template #default="{ row }">
            <el-progress
              :percentage="row.total_qty ? Math.min(100, Math.round((row.received_qty / row.total_qty) * 100)) : 0"
              :stroke-width="8"
              :status="row.received_qty >= row.total_qty ? 'success' : undefined"
            />
            <span class="yc-inb__qty">{{ row.received_qty }} / {{ row.total_qty }}</span>
          </template>
        </el-table-column>
        <el-table-column label="期望到货" min-width="150">
          <template #default="{ row }">{{ row.expected_at ? formatDateTime(row.expected_at) : '—' }}</template>
        </el-table-column>
        <el-table-column prop="created_by" label="创建人" width="110" />
        <el-table-column label="创建时间" min-width="155">
          <template #default="{ row }">{{ formatDateTime(row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="300" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click="open(row)">详情</el-button>
            <el-button
              v-perm="'wms:inbound:update'"
              v-if="canEdit(row.status)"
              link
              type="primary"
              @click="openEdit(row)"
            >
              编辑
            </el-button>
            <el-button
              v-perm="'wms:inbound:release'"
              v-if="canEdit(row.status)"
              link
              type="primary"
              @click="act(row, 'release', `入库单「${row.doc_no}」已释放`)"
            >
              释放
            </el-button>
            <el-button
              v-perm="'wms:inbound:close'"
              v-if="canClose(row.status)"
              link
              type="warning"
              @click="act(row, 'close', `入库单「${row.doc_no}」已关闭`)"
            >
              关闭
            </el-button>
            <el-popconfirm
              v-if="canCancel(row.status)"
              title="确认取消该入库单？"
              width="200"
              @confirm="act(row, 'cancel', `入库单「${row.doc_no}」已取消`)"
            >
              <template #reference>
                <el-button v-perm="'wms:inbound:cancel'" link type="danger">取消</el-button>
              </template>
            </el-popconfirm>
            <el-popconfirm v-if="canEdit(row.status)" title="确认删除该入库单？" width="200" @confirm="remove(row)">
              <template #reference>
                <el-button v-perm="'wms:inbound:delete'" link type="danger">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState v-else-if="!loading" title="还没有入库单" description="先建一张入库单，释放后就能开始收货上架。">
        <el-button v-perm="'wms:inbound:create'" type="primary" @click="openCreate">新建入库单</el-button>
      </EmptyState>
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-inb__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <el-dialog
      v-if="formOpen"
      v-model="formOpen"
      :title="editing ? `编辑入库单 · ${editing.doc_no}` : '新建入库单'"
      width="720px"
      :close-on-click-modal="false"
    >
      <div v-if="formError" class="yc-inb__error">
        <TraceIdAlert :error="formError" />
      </div>

      <el-form label-position="top" @submit.prevent="save">
        <div class="yc-inb__formrow">
          <el-form-item label="所属仓库">
            <el-select v-model="form.warehouse_id" class="yc-inb__ref">
              <el-option v-for="w in warehouses" :key="w.id" :label="label(w.id, warehouses)" :value="w.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="供应商">
            <el-select v-model="form.supplier_id" class="yc-inb__ref">
              <el-option v-for="s in suppliers" :key="s.id" :label="label(s.id, suppliers)" :value="s.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="期望到货时间">
            <el-date-picker
              v-model="form.expected_at"
              type="datetime"
              value-format="YYYY-MM-DD HH:mm:ss"
              placeholder="可留空"
            />
          </el-form-item>
        </div>

        <el-form-item label="备注">
          <el-input v-model="form.remark" maxlength="255" placeholder="可留空" />
        </el-form-item>

        <el-form-item label="入库明细">
          <div class="yc-inb__lines">
            <div v-for="(line, index) in form.lines" :key="index" class="yc-inb__line">
              <el-select v-model="line.sku_id" class="yc-inb__sku" filterable placeholder="选择商品">
                <el-option
                  v-for="s in skus"
                  :key="s.id"
                  :label="`${s.code} ${s.name}`"
                  :value="s.id"
                />
              </el-select>
              <el-input-number v-model="line.qty" :min="1" :max="999999" controls-position="right" />
              <el-button link type="danger" @click="removeLine(index)">删除</el-button>
            </div>
            <div class="yc-inb__line">
              <el-button @click="addLine">添加一行</el-button>
              <span class="yc-inb__hint">同一商品重复添加会自动合并数量</span>
            </div>
          </div>
        </el-form-item>

        <el-button type="primary" native-type="submit" :loading="saving" :disabled="!!formErrorText">
          {{ editing ? '保存' : '创建' }}
        </el-button>
        <span v-if="formErrorText" class="yc-inb__hint yc-inb__hint--inline">{{ formErrorText }}</span>
      </el-form>

      <template #footer>
        <el-button @click="formOpen = false">取消</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-inb {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 170px;
  }

  &__ref {
    width: 190px;
  }

  &__status {
    width: 140px;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__qty {
    display: block;
    margin-top: var(--yc-space-1);
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }

  &__formrow {
    display: flex;
    flex-wrap: wrap;
    gap: var(--yc-space-3);

    .el-form-item {
      margin-bottom: var(--yc-space-3);
    }
  }

  &__lines {
    width: 100%;
  }

  &__line {
    display: flex;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-2);
  }

  &__sku {
    flex: 1;
  }

  &__hint {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
    line-height: 1.6;

    &--inline {
      margin-left: var(--yc-space-3);
    }
  }
}
</style>
